import React, { useMemo } from 'react';
import styled from 'styled-components';
import { COLOR_SYSTEM } from '../../design/design-system';
import { Drug, Prescription, PrescriptionGroup } from '../../../common/models/types';
import PrescriptionTableDisplay, {
  EndedDrugBadge,
  ReviewIncludedBadge,
} from '../../molecules/PrescriptionTableDisplay';
import type { PrintOptions } from './index';
import {
  filterPrescriptionGroupsForPrint,
  getMentionedAdditionGroups,
  isOngoingOn,
  parseDate,
  MentionedAddition,
} from '../../../utils/prescriptionPrintScope';

/**
 * 복용약 정보 섹션 본체 — 범위 필터(처방전 단위)·언급 처방전 병합·상황별 캡션·
 * 언급 약 참고 소표·배지 범례를 한 곳에서 조립한다.
 * 약사 인쇄 화면(PrintContent)과 케어링노트 탑재뷰(CaringNoteEmbedPage)가 공유한다.
 * 설계: docs/ai-discussions/20260904-print-drug-scope-filter.md
 *
 * 섹션 제목(SectionTitle)과 페이지 나눔 래퍼는 각 소비자의 레이아웃 책임이라 여기 두지 않는다.
 */

/* 배지 범례 각주 — 처방 정보 섹션 맨 아래 1회, 실물 배지 미리보기 포함 */
const BadgeLegendLine = styled.p`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 11px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  margin: 12px 0 0 0;

  ${EndedDrugBadge}, ${ReviewIncludedBadge} {
    margin-left: 0;
  }
`;

/* "보고서에 언급된 과거 약 (참고)" 소표 — 본표에 없는 언급 약을 품목당 최신 처방 1건으로 표시 */
const MentionedRefSection = styled.div`
  margin-top: 20px;

  @media print {
    page-break-inside: avoid;
  }
`;

const MentionedRefTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  margin: 0 0 8px 0;
`;

const MentionedRefTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};

  th, td {
    border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
    padding: 6px 8px;
    text-align: left;
  }

  th {
    background: ${COLOR_SYSTEM.get("Gray")?.[50]};
    font-weight: 600;
  }
`;

// 참고 표 용량·용법 요약 (예: "10mg · 1일 2회 · 30일분 · 아침저녁 식후")
const dosageSummary = (rx: Prescription): string => {
  const parts: string[] = [];
  if (rx.dosagePerAdministration) {
    parts.push(`${rx.dosagePerAdministration}${rx.dosageUnit || ''}`);
  }
  if (rx.timesPerDay) parts.push(`1일 ${rx.timesPerDay}회`);
  if (rx.daysOfAdministration) parts.push(`${rx.daysOfAdministration}일분`);
  if (rx.usageInstruction) parts.push(rx.usageInstruction);
  return parts.length > 0 ? parts.join(' · ') : '-';
};

interface PrescriptionScopeSectionProps {
  prescriptionGroups: PrescriptionGroup[];
  otc: any[];
  hff: any[];
  printOptions: PrintOptions;
  drugsData?: Drug[];
  /** "보고서 언급으로 추가될 약" 목록(품목당 최신 처방 1건) — 참고 표에 표시 */
  mentionedAdditions?: MentionedAddition[];
}

const PrescriptionScopeSection: React.FC<PrescriptionScopeSectionProps> = ({
  prescriptionGroups,
  otc,
  hff,
  printOptions,
  drugsData,
  mentionedAdditions = [],
}) => {
  /**
   * 복용약 범위 필터 적용 — 처방전 단위 (처방의약품만. OTC·건기식은 투약일수 개념이 없어 항상 표시)
   */
  const scopeResult = useMemo(() => {
    return filterPrescriptionGroupsForPrint(
      prescriptionGroups,
      printOptions.drugScope,
      new Date()
    );
  }, [prescriptionGroups, printOptions.drugScope]);

  // 참고 섹션 표시 여부 — 복용중 범위 + 추가 옵션 켜짐 + 추가할 약 존재
  const showMentionedRef =
    printOptions.drugScope === 'ongoing' &&
    printOptions.drugScopeAddMentioned &&
    mentionedAdditions.length > 0;

  // "마지막 처방전 통째" 모드 — 언급 약의 최신 처방이 속한 처방전들 (dedupe).
  // 별도 섹션이 아니라 본표의 날짜 위치에 병합한다 (약사가 시계열 맥락에서 읽도록).
  // 구분은 해당 약 행의 "검토 포함" 배지가 담당 — 지니님 확정, 2026-09-05
  const mentionedGroups = useMemo(() => {
    if (!showMentionedRef || printOptions.drugScopeMentionedStyle !== 'group') return [];
    return getMentionedAdditionGroups(prescriptionGroups, mentionedAdditions);
  }, [showMentionedRef, printOptions.drugScopeMentionedStyle, prescriptionGroups, mentionedAdditions]);

  // 본표에 실제로 표시할 그룹 — 복용중 처방전 + (통째 모드면) 언급 처방전을 원본 순서대로 병합.
  // 두 목록 다 원본 배열의 부분집합(참조 동일)이라 원본을 다시 필터하면 날짜 순서가 그대로 보존된다
  const displayGroups = useMemo(() => {
    if (mentionedGroups.length === 0) return scopeResult.groups;
    const shown = new Set([...scopeResult.groups, ...mentionedGroups]);
    return prescriptionGroups.filter((g) => shown.has(g));
  }, [scopeResult.groups, mentionedGroups, prescriptionGroups]);

  // 통째 모드에서 "검토 포함" 행 배지 대상 — 언급 약의 itemCode (해당 약 행에만 붙는다)
  const mentionedRowItemCodes = useMemo(
    () =>
      printOptions.drugScopeMentionedStyle === 'group' && mentionedGroups.length > 0
        ? mentionedAdditions.map((a) => a.itemCode)
        : undefined,
    [printOptions.drugScopeMentionedStyle, mentionedGroups.length, mentionedAdditions]
  );

  // 범위 주석 문구 (scope='all'이면 미표기)
  // 범위 안내는 상황별 서술형 한 문장 (2026-09-05 지니님 확정 — 배지 범례는 섹션 하단 각주로 분리)
  const scopeCaption = (() => {
    if (printOptions.drugScope !== 'ongoing') return null;
    // 범위 필터는 처방의약품에만 적용 — 일반의약품·건기식이 있으면 오해 방지 문장을 덧붙인다
    const otcHffNote =
      otc.length > 0 || hff.length > 0
        ? ' 일반의약품·건강기능식품은 범위와 관계없이 모두 표시됩니다.'
        : '';
    if (scopeResult.shownGroups > 0) {
      // 통째 병합으로 지면 처방전 수가 복용중 건수보다 많아지면 두 건수를 분리 표기한다
      // (캡션 건수 ≠ 실제 표시 건수인 자기모순 방지). 병합 그룹이 복용중 그룹과 겹칠 수 있어
      // mentionedGroups.length가 아니라 실제 지면 증가분(displayGroups − shownGroups)으로 센다.
      const mergedCount = displayGroups.length - scopeResult.shownGroups;
      if (mergedCount > 0) {
        return `복용 중으로 예상되는 약이 있는 처방전 ${scopeResult.shownGroups}건에 보고서에서 다룬 약의 처방전 ${mergedCount}건을 더해 표시했습니다 (전체 ${scopeResult.totalGroups}건 중).${otcHffNote}`;
      }
      return `복용 중으로 예상되는 약이 있는 처방전 ${scopeResult.shownGroups}건을 표시했습니다 (전체 ${scopeResult.totalGroups}건 중).${otcHffNote}`;
    }
    // 0건인데 언급 처방전이 병합돼 아래에 표가 이어지는 경우 — "없습니다"만 쓰면 자기모순
    if (mentionedGroups.length > 0) {
      return `복용 중으로 예상되는 약은 없습니다. 아래는 보고서에서 다룬 약의 최근 처방 기록입니다.${otcHffNote}`;
    }
    if (scopeResult.totalGroups > 0) {
      return `복용 중으로 예상되는 약이 포함된 처방전이 없습니다.${otcHffNote}`;
    }
    return null;
  })();

  // 배지 범례 표시 조건 — 해당 배지가 실제 등장할 때만. 그룹 존재만 보면 복용중 행뿐인
  // 지면(배지 0개)에도 각주가 인쇄되므로, 종료 판정 행이 지면에 실재하는지로 판정한다
  const today = new Date();
  const hasEndedRows = displayGroups.some((g) =>
    (g.prescriptions ?? []).some((rx) => !isOngoingOn(rx, today))
  );
  const showEndedLegend = printOptions.markEndedDrugs && hasEndedRows;
  const showReviewLegend =
    printOptions.markEndedDrugs && (mentionedRowItemCodes?.length ?? 0) > 0;

  return (
    <>
      <PrescriptionTableDisplay
        prescriptionGroups={displayGroups}
        otc={otc}
        supplements={hff}
        viewMode={printOptions.prescriptionViewMode}
        showDrugImages={printOptions.showDrugImages}
        drugsData={drugsData}
        showSourceLegend={false}
        prescriptionCaption={scopeCaption ?? undefined}
        mentionedItemCodes={mentionedRowItemCodes}
        markEndedPrescriptions={printOptions.markEndedDrugs}
        classificationDisplay={printOptions.classificationDisplay}
        showSourceBadges={false}
      />
      {showMentionedRef && printOptions.drugScopeMentionedStyle === 'drug' && (
        <MentionedRefSection>
          <MentionedRefTitle>상담에 참고한 과거 약</MentionedRefTitle>
          <MentionedRefTable>
            <thead>
              <tr>
                <th style={{ width: '40%' }}>약물명</th>
                <th style={{ width: '15%' }}>최근 처방일</th>
                <th style={{ width: '20%' }}>병원</th>
                <th style={{ width: '25%' }}>용량·용법</th>
              </tr>
            </thead>
            <tbody>
              {mentionedAdditions.map((a) => (
                <tr key={a.itemCode}>
                  <td>{a.medicationName}</td>
                  <td>
                    {parseDate(a.latestPrescription.prescriptionDate)?.toLocaleDateString('ko-KR') ?? '-'}
                  </td>
                  <td>{a.latestPrescription.hospital || '-'}</td>
                  <td>{dosageSummary(a.latestPrescription)}</td>
                </tr>
              ))}
            </tbody>
          </MentionedRefTable>
        </MentionedRefSection>
      )}
      {(showEndedLegend || showReviewLegend) && (
        <BadgeLegendLine>
          <span>※</span>
          {showEndedLegend && (
            <>
              <EndedDrugBadge>종료 예상</EndedDrugBadge>
              <span>처방일과 투약일수로 추정한 복용 종료</span>
            </>
          )}
          {showEndedLegend && showReviewLegend && <span>·</span>}
          {showReviewLegend && (
            <>
              <ReviewIncludedBadge>검토 포함</ReviewIncludedBadge>
              <span>보고서에서 다룬 약</span>
            </>
          )}
        </BadgeLegendLine>
      )}
    </>
  );
};

export default PrescriptionScopeSection;
